import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import './AdminPage.css';

const NAV = ['Dashboard','Courses','Students','Settings'];

// ── LESSON EDITOR MODAL (module scope — must NOT be inside AdminPage) ──
function LessonEditor({
  lessonForm, setLessonForm,
  downloads, dlLabel, setDlLabel,
  uploading, uploadProgress,
  saving,
  videoInputRef, docInputRef,
  onUploadVideo, onUploadDownload, onRemoveDownload,
  onSave, onClose,
}) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:500,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'24px',overflowY:'auto'}}>
      <div className="card" style={{width:'100%',maxWidth:'700px',padding:'32px',marginTop:'20px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
          <h3 style={{fontSize:'20px',color:'var(--color-text-heading)'}}>Edit Lesson</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>

        {/* TITLE + DURATION */}
        <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:'12px',marginBottom:'16px'}}>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label">Lesson Title</label>
            <input className="form-input" value={lessonForm.title||''} onChange={e=>setLessonForm(f=>({...f,title:e.target.value}))}/>
          </div>
          <div className="form-group" style={{marginBottom:0,minWidth:'90px'}}>
            <label className="form-label">Duration (mins)</label>
            <input className="form-input" type="number" value={lessonForm.duration_mins||8} onChange={e=>setLessonForm(f=>({...f,duration_mins:parseInt(e.target.value)||8}))}/>
          </div>
        </div>

        {/* VIDEO */}
        <div className="form-group">
          <label className="form-label">📹 Video</label>
          <div className="admin-upload-box" onClick={() => !uploading && videoInputRef.current?.click()}>
            {uploading && uploadProgress.includes('ideo') ? (
              <><div className="spinner" style={{width:'24px',height:'24px',margin:'0 auto 8px'}}/><p>{uploadProgress}</p></>
            ) : lessonForm.video_url ? (
              <><div style={{fontSize:'1.5rem',marginBottom:'6px'}}>✅</div>
                <p style={{fontWeight:600,color:'var(--color-success)',marginBottom:'4px'}}>Video uploaded</p>
                <p style={{fontSize:'13px',color:'var(--color-text-body)',wordBreak:'break-all'}}>{lessonForm.video_url}</p>
                <p style={{fontSize:'12px',color:'var(--color-primary)',marginTop:'6px'}}>Click to replace</p></>
            ) : (
              <><div style={{fontSize:'1.8rem',marginBottom:'8px'}}>🎬</div>
                <p style={{fontWeight:600,color:'var(--color-text-heading)',marginBottom:'4px'}}>Upload video</p>
                <p style={{fontSize:'13px',color:'var(--color-text-body)'}}>Click to select MP4 file · Max 2GB</p></>
            )}
          </div>
          <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm"
            style={{display:'none'}} onChange={e=>e.target.files[0]&&onUploadVideo(e.target.files[0])}/>
          <div style={{marginTop:'8px'}}>
            <label className="form-label" style={{fontSize:'12px'}}>Or paste a Vimeo/YouTube embed URL</label>
            <input className="form-input" placeholder="https://player.vimeo.com/video/..."
              value={lessonForm.video_url||''} onChange={e=>setLessonForm(f=>({...f,video_url:e.target.value}))}/>
          </div>
        </div>

        {/* WRITTEN CONTENT */}
        <div className="form-group">
          <label className="form-label">📝 Written Lesson Content</label>
          <textarea className="form-input" rows={6} placeholder="Lesson text shown below the video. Supports basic HTML (e.g. <h3>, <p>, <strong>)."
            value={lessonForm.content||''} onChange={e=>setLessonForm(f=>({...f,content:e.target.value}))}/>
          <span className="form-hint">Shown below the video. Use &lt;h3&gt; for subheadings, &lt;p&gt; for paragraphs. Optional — can be added later.</span>
        </div>

        {/* AI TUTOR CONTEXT */}
        <div className="form-group">
          <label className="form-label">🤖 Amanda AI Tutor Context</label>
          <textarea className="form-input" rows={4}
            placeholder="What this lesson covers, common student questions, and any important caveats. E.g: This lesson covers gross vs net yield. Students often ask what counts as a good yield (6%+ is viable). Always remind students to model their own deal using the downloadable calculator."
            value={lessonForm.tutor_context||''} onChange={e=>setLessonForm(f=>({...f,tutor_context:e.target.value}))}/>
          <span className="form-hint">Amanda AI reads this to answer student questions in context. Better context = better answers.</span>
        </div>

        {/* DOWNLOADABLE RESOURCES */}
        <div className="form-group">
          <label className="form-label">📎 Downloadable Resources</label>
          {downloads.length > 0 && (
            <div className="admin-dl-list">
              {downloads.map(d => (
                <div key={d.id} className="admin-dl-item">
                  <span className="admin-dl-icon">📄</span>
                  <span className="admin-dl-label">{d.label}</span>
                  <a href={d.file_url} target="_blank" rel="noreferrer" className="admin-dl-link">View</a>
                  <button className="admin-dl-remove" onClick={() => onRemoveDownload(d.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="admin-upload-dl-row">
            <input className="form-input" placeholder="Label students will see, e.g. Yield Calculator (Excel)"
              value={dlLabel} onChange={e=>setDlLabel(e.target.value)} style={{flex:1}}/>
            <button className="btn btn-outline btn-sm" onClick={() => !uploading && docInputRef.current?.click()}
              disabled={uploading || !dlLabel.trim()}>
              {uploading && !uploadProgress.includes('ideo') ? uploadProgress : '⬆ Upload File'}
            </button>
          </div>
          <input ref={docInputRef} type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            style={{display:'none'}} onChange={e=>e.target.files[0]&&onUploadDownload(e.target.files[0])}/>
          <span className="form-hint">PDF, Word, Excel, or PowerPoint · Max 100MB per file · Enter a label first, then click Upload File</span>
        </div>

        {/* PUBLISHED TOGGLE */}
        <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'24px'}}>
          <label className="form-label" style={{margin:0}}>Published (visible to students)</label>
          <div className={`admin-toggle ${lessonForm.is_published?'on':''}`}
            onClick={()=>setLessonForm(f=>({...f,is_published:!f.is_published}))}>
            <div className="admin-toggle-knob"/>
          </div>
          <span style={{fontSize:'13px',color:lessonForm.is_published?'var(--color-success)':'var(--color-text-body)'}}>
            {lessonForm.is_published?'Published':'Draft'}
          </span>
        </div>

        <div style={{display:'flex',gap:'10px'}}>
          <button className={`btn btn-primary ${saving?'btn-loading':''}`} onClick={onSave} disabled={saving}>
            {saving?'':'Save Lesson'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── ADD LESSON MODAL (module scope — must NOT be inside AdminPage) ──
function AddLessonModal({ newLesson, setNewLesson, onSave, onClose }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:500,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'24px',overflowY:'auto'}}>
      <div className="card" style={{width:'100%',maxWidth:'560px',padding:'32px',marginTop:'20px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
          <h3 style={{fontSize:'20px',color:'var(--color-text-heading)'}}>Add Lesson</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>

        <div className="form-group">
          <label className="form-label">Lesson Title</label>
          <input
            className="form-input"
            placeholder="e.g. How to Value a Rental Property"
            value={newLesson.title}
            onChange={e=>setNewLesson(l=>({...l,title:e.target.value}))}
            autoFocus
          />
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          <div className="form-group">
            <label className="form-label">Duration (mins)</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={newLesson.duration_mins}
              onChange={e=>setNewLesson(l=>({...l,duration_mins:parseInt(e.target.value)||8}))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sort Order</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={newLesson.sort_order||1}
              onChange={e=>setNewLesson(l=>({...l,sort_order:parseInt(e.target.value)||1}))}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">🤖 Amanda AI Tutor Context (optional)</label>
          <textarea
            className="form-input"
            rows={3}
            placeholder="What Amanda should know about this lesson — key topics and common student questions."
            value={newLesson.tutor_context}
            onChange={e=>setNewLesson(l=>({...l,tutor_context:e.target.value}))}
          />
          <span className="form-hint">Can be added later via Edit Lesson. You can also upload the video after creating.</span>
        </div>

        <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
          <button className="btn btn-primary" onClick={onSave}>Add Lesson</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();

  const [tab, setTab]         = useState('Dashboard');
  const [stats, setStats]     = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lesson editor state
  const [editingLesson, setEditingLesson]   = useState(null);
  const [lessonForm, setLessonForm]         = useState({});
  const [downloads, setDownloads]           = useState([]);
  const [saving, setSaving]                 = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const videoInputRef = useRef();
  const docInputRef   = useRef();
  const [dlLabel, setDlLabel] = useState('');

  // Add module state
  const [showAddModule, setShowAddModule] = useState(false);
  const [newMod, setNewMod] = useState({ title:'', tutor_context:'' });

  // Add lesson state
  const [addLessonModId, setAddLessonModId] = useState(null);
  const [newLesson, setNewLesson] = useState({ title:'', duration_mins:8, tutor_context:'' });

  useEffect(() => {
    if (!isAdmin) { nav('/dashboard'); return; }
    loadAll();
  }, [isAdmin]);

  const loadAll = async () => {
    try {
      const [sRes, cRes, stRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/courses'),
        api.get('/admin/students'),
      ]);
      setStats(sRes.data);
      setCourses(cRes.data);
      setStudents(stRes.data);
    } finally { setLoading(false); }
  };

  // ── LESSON EDITOR ──
  const openLessonEditor = async (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      title:         lesson.title || '',
      video_url:     lesson.video_url || '',
      content:       lesson.content || '',
      tutor_context: lesson.tutor_context || '',
      duration_mins: lesson.duration_mins || 8,
      is_published:  lesson.is_published !== false,
    });
    try {
      const { data } = await api.get(`/upload/downloads/${lesson.id}`);
      setDownloads(data);
    } catch { setDownloads([]); }
  };

  const saveLesson = async () => {
    if (!editingLesson) return;
    setSaving(true);
    try {
      await api.put(`/admin/lessons/${editingLesson.id}`, lessonForm);
      toast('✓ Lesson saved', 'success');
      setEditingLesson(null);
      loadAll();
    } catch { toast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  // ── VIDEO UPLOAD ──
  const uploadVideo = async (file) => {
    if (!file || !editingLesson) return;
    setUploading(true);
    setUploadProgress('Uploading video…');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('lesson_id', editingLesson.id);
      const { data } = await api.post('/upload/lesson-video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round(e.loaded / e.total * 100);
          setUploadProgress(`Uploading… ${pct}%`);
        },
      });
      setLessonForm(f => ({ ...f, video_url: data.lesson.video_url }));
      toast('✓ Video uploaded', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Video upload failed', 'error');
    } finally { setUploading(false); setUploadProgress(''); }
  };

  // ── DOCUMENT UPLOAD ──
  const uploadDownload = async (file) => {
    if (!file || !editingLesson) return;
    if (!dlLabel.trim()) { toast('Enter a label for this download first', 'error'); return; }
    setUploading(true);
    setUploadProgress('Uploading file…');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('lesson_id', editingLesson.id);
      form.append('label', dlLabel.trim());
      const { data } = await api.post('/upload/download', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDownloads(d => [...d, data.download]);
      setDlLabel('');
      toast('✓ Download added', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Upload failed', 'error');
    } finally { setUploading(false); setUploadProgress(''); }
  };

  const removeDownload = async (id) => {
    try {
      await api.delete(`/upload/download/${id}`);
      setDownloads(d => d.filter(x => x.id !== id));
      toast('Download removed', 'success');
    } catch { toast('Failed to remove', 'error'); }
  };

  // ── ADD MODULE ──
  const addModule = async () => {
    if (!newMod.title.trim()) { toast('Module title required', 'error'); return; }
    try {
      const courseId = courses[0]?.id;
      if (!courseId) { toast('No course found', 'error'); return; }
      await api.post('/admin/modules', {
        course_id: courseId,
        title: newMod.title,
        tutor_context: newMod.tutor_context,
        sort_order: (courses[0]?.modules?.length || 0) + 1,
      });
      toast('Module added ✓', 'success');
      setNewMod({ title:'', tutor_context:'' });
      setShowAddModule(false);
      loadAll();
    } catch { toast('Failed to add module', 'error'); }
  };

  const toggleCourseStatus = async (courseId, current) => {
    const next = current === 'live' ? 'draft' : 'live';
    try {
      await api.put(`/admin/courses/${courseId}`, { status: next });
      toast(`Course ${next === 'live' ? 'published ✓' : 'unpublished'}`, 'success');
      loadAll();
    } catch { toast('Failed to update', 'error'); }
  };

  // ── ADD LESSON ──
  const openAddLesson = (moduleId, existingLessonCount) => {
    setAddLessonModId(moduleId);
    setNewLesson({
      title: '',
      duration_mins: 8,
      tutor_context: '',
      sort_order: (existingLessonCount || 0) + 1,
    });
  };

  const closeAddLesson = () => {
    setAddLessonModId(null);
    setNewLesson({ title:'', duration_mins:8, tutor_context:'' });
  };

  const saveNewLesson = async () => {
    if (!newLesson.title.trim()) { toast('Lesson title required', 'error'); return; }
    if (!addLessonModId) return;
    try {
      await api.post('/admin/lessons', {
        module_id: addLessonModId,
        title: newLesson.title.trim(),
        sort_order: newLesson.sort_order,
        duration_mins: newLesson.duration_mins || 8,
        tutor_context: newLesson.tutor_context || '',
      });
      toast('Lesson added ✓', 'success');
      closeAddLesson();
      loadAll();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to add lesson', 'error');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  const fmt = (p) => `£${((p||0)/100).toFixed(2)}`;

  return (
    <div className="admin-page">
      {editingLesson && (
        <LessonEditor
          lessonForm={lessonForm}
          setLessonForm={setLessonForm}
          downloads={downloads}
          dlLabel={dlLabel}
          setDlLabel={setDlLabel}
          uploading={uploading}
          uploadProgress={uploadProgress}
          saving={saving}
          videoInputRef={videoInputRef}
          docInputRef={docInputRef}
          onUploadVideo={uploadVideo}
          onUploadDownload={uploadDownload}
          onRemoveDownload={removeDownload}
          onSave={saveLesson}
          onClose={() => setEditingLesson(null)}
        />
      )}
      {addLessonModId && (
        <AddLessonModal
          newLesson={newLesson}
          setNewLesson={setNewLesson}
          onSave={saveNewLesson}
          onClose={closeAddLesson}
        />
      )}

      <aside className="admin-sb">
        <div className="admin-sb-logo">
          PropertyTraining<span>.ai</span>
          <div className="admin-badge">Admin Panel</div>
        </div>
        {NAV.map(n => (
          <div key={n} className={`admin-nav-item ${tab===n?'active':''}`} onClick={() => setTab(n)}>
            {{'Dashboard':'📊','Courses':'📚','Students':'👥','Settings':'⚙️'}[n]} {n}
          </div>
        ))}
        <div className="admin-nav-item" onClick={() => nav('/dashboard')}>← Student View</div>
      </aside>

      <main className="admin-main">

        {/* ── DASHBOARD ── */}
        {tab==='Dashboard' && (<>
          <div className="admin-toprow">
            <h2>Admin Dashboard</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setTab('Courses')}>Manage Courses</button>
          </div>
          <div className="admin-stats">
            {[
              {label:'Total Students', val:stats?.total_students||0},
              {label:'Total Revenue',  val:fmt(stats?.total_revenue_pence)},
              {label:'Enrolments',     val:stats?.total_enrolments||0},
              {label:'Completions',    val:stats?.total_completions||0},
            ].map(s=>(
              <div key={s.label} className="admin-stat card card-p">
                <div className="admin-stat-val">{s.val}</div>
                <div className="admin-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="admin-panel card">
            <div className="admin-panel-head"><h3>Recent Students</h3></div>
            {students.slice(0,5).map(s=>(
              <div key={s.id} className="admin-row">
                <div className="admin-av">{(s.first_name?.[0]||s.email[0]).toUpperCase()}</div>
                <div className="admin-row-info">
                  <div className="admin-row-name">{s.first_name} {s.last_name}</div>
                  <div className="admin-row-meta">{s.email}</div>
                </div>
                <span className="badge badge-teal">{s.lessons_completed} lessons done</span>
                <span style={{fontSize:'13px',color:'#9CA3AF'}}>{new Date(s.created_at).toLocaleDateString('en-GB')}</span>
              </div>
            ))}
            {students.length===0&&<div style={{padding:'24px',textAlign:'center',color:'var(--color-text-body)'}}>No students yet</div>}
          </div>
        </>)}

        {/* ── COURSES ── */}
        {tab==='Courses' && (<>
          <div className="admin-toprow">
            <h2>Courses</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddModule(true)}>+ Add Module</button>
          </div>

          {showAddModule && (
            <div className="admin-add-form card card-p">
              <h4 style={{marginBottom:'14px'}}>Add Module to Course 1</h4>
              <div className="form-group">
                <label className="form-label">Module Title</label>
                <input className="form-input" placeholder="e.g. Understanding Capital Growth"
                  value={newMod.title} onChange={e=>setNewMod(m=>({...m,title:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label className="form-label">AI Tutor Context</label>
                <textarea className="form-input" placeholder="What Amanda should know about this module…"
                  value={newMod.tutor_context} onChange={e=>setNewMod(m=>({...m,tutor_context:e.target.value}))}/>
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button className="btn btn-primary btn-sm" onClick={addModule}>Save Module</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddModule(false)}>Cancel</button>
              </div>
            </div>
          )}

          {courses.map(course=>(
            <div key={course.id} className="admin-panel card">
              <div className="admin-panel-head">
                <h3>{course.title}</h3>
                <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                  <span className={`badge ${course.status==='live'?'badge-success':'badge-warning'}`}>{course.status}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => toggleCourseStatus(course.id, course.status)}>
                    {course.status==='live'?'Unpublish':'Publish'}
                  </button>
                </div>
              </div>
              {course.modules?.map((m,i)=>(
                <div key={m.id}>
                  <div className="admin-row admin-mod-row">
                    <div className="admin-mod-num">{i+1}</div>
                    <div className="admin-row-info" style={{flex:1}}>
                      <div className="admin-row-name">{m.title}</div>
                      <div className="admin-row-meta">{m.lessons?.filter(l=>l?.id).length||0} lessons</div>
                    </div>
                    <span className={`badge ${m.is_published?'badge-success':'badge-grey'}`}>
                      {m.is_published?'Published':'Draft'}
                    </span>
                  </div>
                  {/* LESSONS */}
                  {m.lessons?.filter(l=>l?.id).map((lesson,li)=>(
                    <div key={lesson.id} className="admin-lesson-row">
                      <span className="admin-lesson-num">{li+1}</span>
                      <div className="admin-row-info" style={{flex:1}}>
                        <div style={{fontSize:'14px',color:'var(--color-text-heading)',fontWeight:500}}>{lesson.title}</div>
                        <div style={{fontSize:'12px',color:'var(--color-text-body)',display:'flex',gap:'12px',marginTop:'2px'}}>
                          <span>{lesson.duration_mins}m</span>
                          <span style={{color:lesson.video_url?'var(--color-success)':'#9CA3AF'}}>
                            {lesson.video_url?'✓ Video':'No video yet'}
                          </span>
                          <span style={{color:lesson.is_published?'var(--color-success)':'var(--color-warning)'}}>
                            {lesson.is_published?'Published':'Draft'}
                          </span>
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => openLessonEditor(lesson)}>
                        ✏️ Edit Lesson
                      </button>
                    </div>
                  ))}
                  {/* ADD LESSON BUTTON */}
                  <div className="admin-lesson-row" style={{justifyContent:'flex-start',paddingLeft:'46px'}}>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => openAddLesson(m.id, m.lessons?.filter(l=>l?.id).length || 0)}
                    >
                      + Add Lesson
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>)}

        {/* ── STUDENTS ── */}
        {tab==='Students' && (<>
          <div className="admin-toprow"><h2>Students ({students.length})</h2></div>
          <div className="admin-panel card">
            <div className="admin-panel-head"><h3>All Students</h3></div>
            {students.map(s=>(
              <div key={s.id} className="admin-row">
                <div className="admin-av">{(s.first_name?.[0]||s.email[0]).toUpperCase()}</div>
                <div className="admin-row-info">
                  <div className="admin-row-name">{s.first_name} {s.last_name}</div>
                  <div className="admin-row-meta">{s.email}</div>
                </div>
                <span className="badge badge-teal">{s.courses_enrolled} course{s.courses_enrolled!==1?'s':''}</span>
                <span style={{fontSize:'13px',color:'#9CA3AF'}}>{new Date(s.created_at).toLocaleDateString('en-GB')}</span>
              </div>
            ))}
            {students.length===0&&<div style={{padding:'24px',textAlign:'center',color:'var(--color-text-body)'}}>No students yet</div>}
          </div>
        </>)}

        {/* ── SETTINGS ── */}
        {tab==='Settings' && (<>
          <div className="admin-toprow"><h2>Settings</h2></div>
          <div className="card card-p" style={{maxWidth:'480px'}}>
            <h3 style={{marginBottom:'16px'}}>Admin Account</h3>
            <p style={{fontSize:'15px',marginBottom:'8px'}}><strong>Email:</strong> {user?.email}</p>
            <p style={{fontSize:'15px',marginBottom:'16px'}}><strong>Role:</strong> Administrator</p>
            <p style={{fontSize:'14px',color:'var(--color-text-body)'}}>To add additional admin users or change passwords, update the users table via your developer or database panel.</p>
          </div>
        </>)}

      </main>
    </div>
  );
}